import { eq } from "drizzle-orm";
import { type PowerSyncDrizzleDB, user } from "@time-tracker/powersync";

export interface UserPreferences {
  /**
   * Owner of the row. It is in the SELECT on purpose: in multi-account mode the
   * query re-runs when the account changes but KEEPS the previous data until it
   * resolves the new one, so without this field the consumer cannot distinguish
   * the active account's preferences from the PREVIOUS account's (language,
   * theme, font…). See the filter in `user-preferences-context`.
   */
  id: string;
  locale: string | null;
  global_theme_id: string | null;
  is_blur: number | null;
  follow_device_theme: number | null;
  font_family: string | null;
  /**
   * Avatar storage key (`avatars/{userId}/{hash}.webp`).
   *
   * It is here on purpose and not read from the session: the session is served by the API
   * and remains cached, so a freshly uploaded photo would not appear until the
   * next refresh. Reading it from the local row —like language, font, and theme—
   * makes the photo appear immediately and survive offline.
   */
  image: string | null;
}

/**
 * Shared Drizzle query builder for User preferences.
 */
export function getUserPreferencesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      id: user.id,
      locale: user.locale,
      global_theme_id: user.global_theme_id,
      is_blur: user.is_blur,
      follow_device_theme: user.follow_device_theme,
      font_family: user.font_family,
      image: user.image,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User locale.
 */
export function getUserLocaleQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      locale: user.locale,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User font family preference.
 */
export function getUserFontFamilyQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      font_family: user.font_family,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User theme settings.
 *
 * It ALWAYS filters by `userId`, like the rest of the reads of `user` in this
 * module. Previously it decided the filter in a branch and the guest was left with
 * `SELECT … FROM user LIMIT 1` bare: without `WHERE`, SQLite returns the FIRST
 * row it happens to find, which need not be the guest's. That is not a correctness
 * failure but a data leak between accounts: during the guest→real upgrade the
 * local database drops one `user` row for each fellow member of the organization,
 * so the guest ended up displaying ANOTHER person's theme.
 * Without `WHERE` there is no possible empty result: someone's row always comes out.
 */
export function getUserThemeSettingsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      global_theme_id: user.global_theme_id,
      is_blur: user.is_blur,
      follow_device_theme: user.follow_device_theme,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}
